import { useContext } from 'react';
import timeStampToDate from '@/helpers/timeStampToDate';
import { getColor, isFirstRow, isLastRow } from '@/helpers/utils';

import { setSerp } from '@/app/Serp/reducers/serpDataSlice';

import numberHumanizer from '@/helpers/numberHumanizer';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { Doughnut } from 'react-chartjs-2';
import { Link, useNavigate } from 'react-router-dom';
import type {
  ColDef,
  ColumnMenuTab,
  ICellRendererParams,
  ValueFormatterParams
} from 'ag-grid-community';
import {
  decodeGetter,
  valueFormatter
} from '@/app/common/components/ag-grid/configs';
import { serpDifficuly } from '@/helpers/serpDifficulity';
import URLRenderer from '@/app/common/components/CellRenderers/URLRenderer';
import SerpSnippetIcons from '@/app/Serp/components/SerpSnippetIcons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown } from 'react-bootstrap';
import { warningMessage } from '@/utils/toast';
import { NewSerpContext, RemoveSerpContext } from '@/context/Context';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import historyApi, {
  useStartSerpGettingMutation
} from '@/app/Serp/api/historyApi';
import SpamScoreCellRenderer from './SpamScoreCellRenderer';
import DomainAuthorityCellRenderer from './DomainAuthorityCellRenderer';
import DomainRankCellRenderer from './DomainRankCellRenderer';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import SearchVolumeChart from '@/app/common/components/CellRenderers/SearchVolumeChart';
import { useDispatch } from 'react-redux';
import accessLevelApi from '@/app/common/api/accessLevelApi';
import { commonTooltips } from '@/app/common/tooltip/commonTooltips';
import { serpTooltips } from '@/app/Serp/tooltip/serpTooltips';
import type { SerpHistoryItem, SerpItem } from '@/app/Serp/types/serpApiTypes';

type SerpHistoryColumn = Omit<ColDef<SerpHistoryItem>, 'field'> & {
  field?: string;
  searchable?: boolean;
  title?: string;
  type?: string;
  noFilter?: boolean;
};

type SerpItemColumn = ColDef<SerpItem> & {
  searchable?: boolean;
  title?: string;
  type?: string;
  noFilter?: boolean;
  tooltip?: string;
};

const menuTabs: ColumnMenuTab[] = [
  'columnsMenuTab',
  'filterMenuTab',
  'generalMenuTab'
];

function hostCompanyWebsite(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.href;
  } catch {
    return null;
  }
}

function HostCompanyCell(params: ICellRendererParams<SerpItem>) {
  if (
    params.data?.type !== 'organic' &&
    params.data?.type !== 'feature_snippet'
  ) {
    return null;
  }
  if (params.value === '-1') return <TextLoading />;
  const name =
    params.value == null || params.value === '' ? '' : String(params.value);
  if (!name) return null;
  const website = hostCompanyWebsite(params.data?.host_company_website);
  if (!website) return name;
  return (
    <a href={website} target="_blank" rel="noreferrer">
      {name}
    </a>
  );
}

export const HistoryColumns = (
  domain_slug: string | null = null,
  domain: string | null = null
): SerpHistoryColumn[] => [
  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterValueGetter: decodeGetter,
    field: 'keyword',
    type: 'string',
    title: 'کلمه کلیدی',
    cellStyle: { textAlign: 'center', margin: '0px' },
    cellClass: 'over-flow',
    cellRenderer: (params: ICellRendererParams<SerpHistoryItem>) => {
      return (
        <Link
          to={`/serp/overview/${domain_slug}/${domain}/${params.data?.id}/${params.data?.location_code}/${params.data?.language_code}/${decodeURIComponent(params.data?.keyword ?? '')}`}
          style={{
            color: getColor('navbar-vertical-default-link-active-color')
          }}
          className={`fw-bolder btn-link ${!params.data?.id && 'disabled-link'}`}
        >
          <div className="d-flex gap-1 align-items-baseline">
            {valueFormatter(params)}
          </div>
        </Link>
      );
    },
    flex: 1,
    minWidth: 150
  },
  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,

    filterValueGetter: decodeGetter,
    field: 'location_code',
    type: 'string',
    title: 'مکان جستجو',
    width: 125,
    cellRenderer: (params: ICellRendererParams<SerpHistoryItem>) => (
      <div className="d-flex gap-2 align-items-center">
        <img src={`https://static.mizfa.tools/flags/${params.value}.webp`} />
        {params.value}
      </div>
    )
  },
  {
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    field: 'search_volume_miztols',
    type: 'number',
    title: 'میزان جستجو (ماه)',
    width: 150,
    searchable: true,
    cellRenderer: (params: ICellRendererParams<SerpHistoryItem>) => (
      <>
        {!params.value ? (
          <HelpTooltip html={commonTooltips.searchVolume} />
        ) : (
          numberHumanizer(params.value)
        )}
      </>
    )
  },
  {
    field: 'search_volume_dates',
    type: 'array',
    title: 'روند جستجو',
    width: 120,
    cellStyle: {
      textAlign: 'center',
      margin: '0px'
    },
    sortable: false,
    cellClass: 'over-flow',
    noFilter: true,
    searchable: false,
    cellRenderer: (params: ICellRendererParams<SerpHistoryItem>) => {
      return params.value ? (
        <SearchVolumeChart
          count={22}
          isFirstRow={isFirstRow(params)}
          isLastRow={isLastRow(params)}
          dates={params.value.slice(-12)}
          series={[
            {
              name: 'روند جستجو',
              data: (params.data?.search_volume_values ?? [])
                .slice(-12)
                .map((value, i) => ({
                  name: (params.data?.search_volume_dates ?? []).slice(-12)[
                    i
                  ],
                  value: value,
                  itemStyle: {
                    color: getColor('navbar-vertical-default-link-active-color')
                  }
                }))
            }
          ]}
        />
      ) : (
        ''
      );
    }
  },
  {
    searchable: false,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,

    filterValueGetter: decodeGetter,
    field: 'keyword_difficulty',
    type: 'string',
    title: 'سختی کلمه کلیدی',
    cellRenderer: (params: ICellRendererParams<SerpHistoryItem>) => (
      <div
        className="d-flex justify-content-between"
        style={{ fontWeight: 'bold' }}
      >
        {params.value ? (
          params.value !== 101 ? (
            <div
              className="fs-"
              style={{
                fontWeight: 'bold',
                color: serpDifficuly(params.value).color
              }}
            >
              {serpDifficuly(params.value).label}{' '}
              <span style={{ direction: 'ltr' }} className="fs--2">
                (%{params.value})
              </span>
            </div>
          ) : (
            <span
              style={{
                color: serpDifficuly(100).color
              }}
            >
              خطا رخ داد!!!
            </span>
          )
        ) : (
          <span
            style={{
              color: serpDifficuly(50).color
            }}
          >
            در حال محاسبه ...
          </span>
        )}
        {params.value && params.value !== 101 && (
          <div style={{ width: '50px', height: '40px' }}>
            <Doughnut
              data={{
                labels: ['Reached', 'Remaining'],
                datasets: [
                  {
                    data: [params.value, 100 - params.value],
                    backgroundColor: [
                      serpDifficuly(params.value).color,
                      getColor('gray-300')
                    ],
                    borderColor: [
                      serpDifficuly(params.value).color,
                      getColor('gray-300')
                    ]
                  }
                ]
              }}
              options={{
                rotation: -90,
                animation: false,
                circumference: 180,
                cutout: '60%',
                hover: { mode: undefined },
                plugins: {
                  legend: {
                    display: false
                  },
                  tooltip: undefined
                }
              }}
              width={310}
            />
          </div>
        )}
      </div>
    )
  },
  {
    title: 'تاریخ',
    searchable: false,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    field: 'inserted_at',
    type: 'string',
    width: 105,
    valueFormatter: (params: ValueFormatterParams<SerpHistoryItem>) =>
      timeStampToDate(Number(params.value)).slice(8).toPersianDigit()
  },
  {
    field: 'url',
    type: 'array',
    title: ' ',
    searchable: false,
    cellStyle: { textAlign: 'center', margin: '0px' },
    cellClass: 'over-flow',
    width: 70,
    pinned: 'left',
    noFilter: true,
    cellRenderer: (params: ICellRendererParams<SerpHistoryItem>) => {
      const dispatch = useDispatch();
      const { data: userInfo } = useGetUserInfoQuery();
      const { setStep, setIsSubmitted, setSerpId } =
        useContext(NewSerpContext);
      const [startSerp] = useStartSerpGettingMutation();
      const {
        setSerpId: setRemSerpId,
        setShow,
        setKeyword: setRemKeyword
      } = useContext(RemoveSerpContext);
      const navigate = useNavigate();
      return (
        <span className="allaho_akbar_zIndex">
          <Dropdown
            onClick={e => e.stopPropagation()}
            align="end"
            className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
          >
            <Dropdown.Toggle
              className="text-600 btn-reveal"
              split
              variant="reveal"
              size="sm"
            >
              <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="border py-0">
              <Dropdown.Item
                as={Link}
                to={`/serp/overview/${domain_slug}/${domain}/${params.data?.id}/${params.data?.location_code}/${params.data?.language_code}/${params.data?.keyword}`}
                className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
              >
                <FontAwesomeIcon icon="eye" className="me-1 ms-2" />
                مشاهده
              </Dropdown.Item>
              <Dropdown.Item
                onClick={async () => {
                  setIsSubmitted(true);
                  setStep(2);
                  window.scrollTo({
                    top: document.documentElement.scrollHeight,
                    behavior: 'smooth'
                  });
                  await startSerp({
                    requested_user: {
                      name_family: `${userInfo?.data?.first_name} ${userInfo?.data?.last_name}`,
                      email: userInfo?.data?.email ?? ''
                    },
                    keyword: decodeURIComponent(params.data?.keyword ?? ''),
                    location_code: params.data?.location_code || 'ir',
                    language_code: params.data?.language_code || 'fa',
                    google_domain: 'google.com',
                    domain_slug,
                    use_agent: null
                  })
                    .unwrap()
                    .then(response => {
                      if (response.code !== 200) {
                        warningMessage(String(response.data));
                        setIsSubmitted(false);
                        setStep(1);
                      } else {
                        setSerpId(response.data.serp_id ?? '');
                        dispatch(setSerp(response.data));

                        navigate(
                          // `/serp/overview/${response.data.serp_id}/${domain_slug}/${domain}`
                          `/serp/overview/${domain_slug}/${domain}/${response.data.serp_id}/${params.data?.location_code}/${params.data?.language_code}/${params.data?.keyword}`
                        );
                        dispatch(
                          historyApi.util.invalidateTags(['SerpStatus'])
                        );
                        dispatch(
                          accessLevelApi.util.invalidateTags(['AccessLevel'])
                        );
                        setStep(3);
                      }
                    })
                    .catch(err => {
                      console.log(err);
                    });
                }}
                className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
              >
                <FontAwesomeIcon icon="undo-alt" className="me-1 ms-2" />
                جستجوی مجدد
              </Dropdown.Item>
              <Dropdown.Divider className="my-0" />
              <Dropdown.Item
                onClick={() => {
                  setRemKeyword(params.data?.keyword ?? '');
                  setRemSerpId(params.data?.id ?? '');
                  setShow(true);
                }}
                className="link-600 text-danger fs--1 d-flex align-items-center gap-1 p-0 px-1"
              >
                <FontAwesomeIcon icon="trash-alt" className="me-1 fs-0 ms-2" />
                حذف
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </span>
      );
    }
  }
];
export const OverviewColumns = (isMdUp?: boolean): SerpItemColumn[] => {
  const defCols: SerpItemColumn[] = [
    {
      searchable: false,
      field: 'rank_absolute',
      type: 'number',
      title: 'رتبه',
      width: 73
    },
    {
      searchable: false,
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,

      filterValueGetter: decodeGetter,
      field: 'fa_type',
      type: 'string',
      title: 'نوع نتیجه',
      width: 110
    },
    {
      searchable: true,
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,

      filterValueGetter: decodeGetter,
      field: 'title',
      type: 'string',
      title: 'عنوان سئو',
      minWidth: 170,
      flex: 1
    },
    {
      searchable: true,
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterValueGetter: decodeGetter,
      field: 'host_company',
      type: 'string',
      title: 'شرکت هاست',
      cellRenderer: HostCompanyCell,
      width: 160
    },
    {
      searchable: true,
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,

      filterValueGetter: decodeGetter,
      field: 'host_location',
      type: 'string',
      title: 'مکان هاست',
      cellRenderer: (params: ICellRendererParams<SerpItem>) =>
        params.data?.type === 'organic' ||
        params.data?.type === 'feature_snippet' ? (
          params.value !== '-1' ? (
            params.value !== '-' &&
            params.value && (
              <div className="d-flex gap-2 align-items-center">
                <img
                  src={`https://static.mizfa.tools/flags/${String(params.value).toLowerCase()}.webp`}
                />
                <span> {params.value}</span>
              </div>
            )
          ) : (
            <TextLoading />
          )
        ) : (
          ''
        ),
      width: 120
    },
    {
      searchable: true,
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,

      filterValueGetter: decodeGetter,
      field: 'serp_snippets',
      type: 'string',
      title: 'ویژگی نتایج',
      minWidth: 160,
      cellRenderer: (params: ICellRendererParams<SerpItem>) => (
        <SerpSnippetIcons data={params.value} />
      )
    },
    {
      searchable: false,
      menuTabs,
      filter: 'agTextColumnFilter',
      floatingFilter: true,

      field: 'domain_rank',
      type: 'number',
      title: 'DR (قدرت دامنه)',
      tooltip: serpTooltips.domainRank,
      width: 160,
      pinned: isMdUp ? 'left' : undefined,
      cellRenderer: (params: ICellRendererParams<SerpItem>) =>
        params.data?.type === 'organic' ||
        params.data?.type === 'feature_snippet' ? (
          params.value !== -10 ? (
            <DomainRankCellRenderer num={params.value} />
          ) : (
            <TextLoading />
          )
        ) : (
          ''
        )
    }
  ];
  const urlCol: SerpItemColumn = {
    searchable: false,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,

    filterValueGetter: decodeGetter,
    field: 'url',
    type: 'string',
    title: 'نشانی',
    pinned: isMdUp ? 'left' : undefined,
    minWidth: 150,
    cellRenderer: (params: ICellRendererParams<SerpItem>) => (
      <URLRenderer url={params.value} />
    )
  };
  const copyCols = [...defCols];
  isMdUp ? copyCols.push(urlCol) : copyCols.unshift(urlCol);
  return copyCols;
};
export const ResultsConfig: SerpItemColumn[] = [
  {
    searchable: false,
    // menuTabs,
    // filter: 'agTextColumnFilter',
    // floatingFilter: true,

    field: 'rank_absolute',
    type: 'number',
    title: 'رتبه',
    width: 73
  },
  {
    searchable: false,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,

    filterValueGetter: decodeGetter,
    field: 'fa_type',
    type: 'string',
    title: 'نوع نتیجه',
    width: 110
  },
  {
    searchable: false,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,

    field: 'domain_rank',
    type: 'number',
    title: 'DR (قدرت دامنه)',
    tooltip: serpTooltips.domainRank,
    width: 160,
    cellRenderer: (params: ICellRendererParams<SerpItem>) =>
      params.data?.type === 'organic' ||
      params.data?.type === 'feature_snippet' ? (
        params.value !== -10 ? (
          <DomainRankCellRenderer num={params.value} />
        ) : (
          <TextLoading />
        )
      ) : (
        ''
      )
  },
  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,

    field: 'moz_domain_authority',
    type: 'number',
    title: 'دامین آتوریتی',
    width: 120,
    cellRenderer: (params: ICellRendererParams<SerpItem>) =>
      params.data?.type === 'organic' && (
        <DomainAuthorityCellRenderer num={params.value} />
      )
  },
  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,

    field: 'moz_spam_score',
    type: 'number',
    title: 'اسپم اسکور',
    width: 120,
    cellRenderer: (params: ICellRendererParams<SerpItem>) =>
      params.data?.type === 'organic' && (
        <SpamScoreCellRenderer num={params.value} />
      )
  },
  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterValueGetter: decodeGetter,
    field: 'host_company',
    type: 'string',
    title: 'شرکت هاست',
    width: 160,
    cellRenderer: HostCompanyCell
  },
  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterValueGetter: decodeGetter,
    field: 'host_location',
    type: 'string',
    title: 'مکان هاست',
    cellRenderer: (params: ICellRendererParams<SerpItem>) =>
      params.data?.type === 'organic' ||
      params.data?.type === 'feature_snippet' ? (
        params.value !== '-1' ? (
          params.value !== '-' &&
          params.value && (
            <div className="d-flex gap-2 align-items-center">
              <img
                src={`https://static.mizfa.tools/flags/${String(params.value).toLowerCase()}.webp`}
              />
              <span> {params.value}</span>
            </div>
          )
        ) : (
          <TextLoading />
        )
      ) : (
        ''
      ),
    width: 120
  },
  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,

    field: 'domain_age',
    type: 'number',
    title: 'سن دامنه (سال)',
    width: 120
  },
  {
    searchable: false,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,

    filterValueGetter: decodeGetter,
    field: 'domain_suffix',
    type: 'string',
    title: 'پسوند دامنه',
    width: 110
  },
  {
    searchable: false,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,

    filterValueGetter: decodeGetter,
    field: 'domain_expiration',
    type: 'string',
    title: 'انقضای دامنه',
    width: 110
  },
  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,

    filterValueGetter: decodeGetter,
    field: 'serp_snippets',
    type: 'string',
    title: 'ویژگی نتایج',
    minWidth: 160,
    cellRenderer: (params: ICellRendererParams<SerpItem>) => (
      <SerpSnippetIcons data={params.value} />
    )
  },
  {
    searchable: true,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,

    filterValueGetter: decodeGetter,
    field: 'title',
    type: 'string',
    title: 'عنوان سئو',
    minWidth: 350
  },
  {
    searchable: false,
    menuTabs,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    width: 150,
    filterValueGetter: decodeGetter,
    field: 'url',
    type: 'string',
    title: 'نشانی',
    pinned: 'left',
    cellRenderer: (params: ICellRendererParams<SerpItem>) => (
      <URLRenderer url={params.value} />
    )
  }
];
